import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupLevels } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedLevelId: string | undefined;
  onChange: (level: SelectedItemDto) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  gameType: string | undefined;
};

const SelectLevel = ({ selectedLevelId, onChange, dateRanges, gameType }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupLevels(dateRanges, gameType);

  const allLevelIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const getLevelName = useCallback(
    (levelId: string) => {
      const levelNames = new Map(data?.map((datum) => [datum.id, datum.name]));
      levelNames.set('', ''); // needed by autocomplete to reset properly
      return levelNames.get(levelId) ?? (t('Nonexistent level') as string);
    },
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Level')}
      options={allLevelIds}
      selected={selectedLevelId ?? ''}
      setSelected={(levelId) => onChange({ id: levelId, name: getLevelName(levelId) })}
      getOptionLabel={(option) => (isLoading ? t('Loading levels...') : getLevelName(option))}
      noOptionsText={isLoading ? t('Loading levels...') : t('No levels found')}
      disabled={!dateRanges || dateRanges.length === 0 || !gameType}
      single
    />
  );
};

export default SelectLevel;
